/*
 * NEXZ V2 — cart analytics (Stage H). Same nexz:* event-hook contract as
 * nexz-pdp.js/nexz-collection.js — no destination wired here.
 */
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';
import { StandardEvents } from '@shopify/events';

const EVENT_CONTRACT = ['view_cart', 'remove_from_cart', 'begin_checkout'];

function nexzEmit(name, detail) {
  if (EVENT_CONTRACT.indexOf(name) === -1) return;
  document.dispatchEvent(new CustomEvent('nexz:' + name, { detail: detail || {}, bubbles: true }));
}

function initDrawerView() {
  const drawer = document.querySelector('theme-drawer#cart-drawer, [id="cart-drawer"]');
  if (!drawer) return;
  drawer.addEventListener(DrawerOpenEvent.eventName, function () {
    nexzEmit('view_cart', {});
  });
}

function initRemove() {
  document.addEventListener(StandardEvents.cartLinesUpdate, function (event) {
    if (event.action !== 'remove' || event.context !== 'cart' || !event.promise) return;
    event.promise.then(function (result) {
      if (!result || (result.detail && result.detail.didError)) return;
      nexzEmit('remove_from_cart', {});
    }, function () {
      // Rejected — no confirmed removal to report.
    });
  });
}

function initCheckout() {
  document.addEventListener('click', function (event) {
    var button = event.target.closest ? event.target.closest('#checkout, [name="checkout"]') : null;
    if (!button) return;
    nexzEmit('begin_checkout', {});
  });
}

// /cart page: no drawer to open, so the page load itself is the view.
// Reuses the native data-template attribute (layout/theme.liquid) rather
// than adding a new marker.
function initCartPageView() {
  var templateEl = document.querySelector('[data-template]');
  if (templateEl && templateEl.getAttribute('data-template') === 'cart') {
    nexzEmit('view_cart', {});
  }
}

/*
 * Safety net for a stuck cart drawer (reported: add to cart -> close the
 * drawer -> page cannot scroll and taps do nothing, Android Chrome).
 *
 * Mechanism being guarded against, not a redesign of the drawer: the
 * drawer panel is a modal <dialog> (showModal()), so while it is open the
 * rest of the page is inert and <html> carries Horizon's `scroll-lock`
 * attribute (base.css: `html[scroll-lock] { overflow: hidden }`).
 * theme-drawer.js only reaches `panel.close()` after
 * `await onAnimationEnd(panel)`, and adding to cart re-renders the drawer
 * through the Section Rendering API — Horizon's own source comments note
 * that "a Section Rendering API morph between open and close can replace
 * the panel". If the panel it is holding is swapped mid-close, the real
 * dialog can stay open and the lock is never released: exactly the
 * reported symptom.
 *
 * This only ever acts on an already-broken state. Every branch below is
 * gated on there being NO drawer still marked open and NO modal that is
 * legitimately open, so it cannot interfere with normal opening/closing.
 */
function healStuckOverlay() {
  // A drawer still marked open is legitimate — never interfere.
  if (document.querySelector('theme-drawer[open]')) return;

  // Close a dialog orphaned inside a drawer that is no longer open.
  var stale = document.querySelectorAll('theme-drawer:not([open]) dialog[open]');
  for (var i = 0; i < stale.length; i++) {
    try {
      stale[i].close();
    } catch (e) {
      // Already closed / not a modal — nothing to recover.
    }
  }

  // Release the scroll lock only when nothing modal remains open. Horizon
  // owns this attribute; clearing a stale one is safe because its own
  // syncScrollLock() re-adds it whenever a real owner still exists.
  if (document.querySelector('dialog[open]')) return;
  if (document.querySelector('[scroll-lock][open]')) return;
  document.documentElement.removeAttribute('scroll-lock');
}

function initStuckOverlayGuard() {
  // DrawerCloseEvent does not bubble, so listen in the capture phase —
  // that still reaches the target and survives the drawer element being
  // replaced by a Section Rendering morph.
  document.addEventListener(
    DrawerCloseEvent.eventName,
    function () {
      // After the close animation, then once more as a backstop.
      setTimeout(healStuckOverlay, 600);
      setTimeout(healStuckOverlay, 1600);
    },
    { capture: true }
  );
}

initDrawerView();
initRemove();
initCheckout();
initCartPageView();
initStuckOverlayGuard();
